// One supporting-indicator row: two related maps side by side. Each column shows
// either its stacked-bar time series or, when the series would just repeat the
// other column's, a longer "trend & classes" analysis panel in its place.

import CustomChart from "../CustomChart";
import SpiChart from "../SpiChart";
import SmaChart from "../SMAChart";
import FaparChart from "../FaparChart";
import ForecastChart from "../ForecastChart";
import UnwdcChart from "../UnwdcChart";
import ReportMap from "./ReportMap";

const TREND_CHARTS = {
  cdi: CustomChart,
  spi: SpiChart,
  sma: SmaChart,
  fapar: FaparChart,
  forecast: ForecastChart,
  unwdc: UnwdcChart,
};

// `forecast` data is the `forecast_summary` object, not a time-series array.
function hasChartData(chart, data) {
  if (chart === "forecast") {
    return Boolean(data && (data.overview?.length || data.percentage_area));
  }
  return Array.isArray(data) && data.length > 0;
}

function TrendChart({ chart, data }) {
  const Chart = TREND_CHARTS[chart];
  if (data === undefined) {
    return (
      <p className="indicator-row__nochart">
        <span className="report-spinner" /> Loading chart…
      </p>
    );
  }
  if (!Chart || !hasChartData(chart, data)) {
    return <p className="indicator-row__nochart">Data unavailable for this period.</p>;
  }
  return <Chart data={data} compact />;
}

/**
 * @param {object} props
 * @param {string} props.title Row heading.
 * @param {Array<{key, label, caption?, chart, series, note?, detail?, map}>} props.maps
 * @param {string[]} props.sharedNotes Interpretation paragraph(s) for the whole row.
 * @param {string|number} props.countryId
 * @param {{minx, miny, maxx, maxy}|null} props.bounds Country bbox for the map images.
 */
export default function IndicatorRow({ title, maps, sharedNotes, countryId, bounds }) {
  return (
    <article className="indicator-row">
      <h3 className="indicator-row__title">{title}</h3>

      <div className="indicator-row__cols">
        {maps.map((m) => (
          <figure key={m.key} className="indicator-row__col">
            <div className="indicator-row__canvas">
              <ReportMap req={m.map} bounds={bounds} countryId={countryId} alt={m.label} />
            </div>
            <figcaption className="indicator-row__cap">
              <span className="indicator-row__map-label">{m.label}</span>
              {m.caption && <span className="indicator-row__map-caption"> · {m.caption}</span>}
            </figcaption>

            {m.chart ? (
              <div className="indicator-row__chart">
                <TrendChart chart={m.chart} data={m.series} />
              </div>
            ) : m.detail ? (
              <div className="indicator-row__detail">
                <p className="indicator-row__note-title">Trend &amp; classes</p>
                <p>{m.detail}</p>
              </div>
            ) : null}

            {m.note && (
              <div className="indicator-row__note">
                <p className="indicator-row__note-title">Analysis</p>
                <p>{m.note}</p>
              </div>
            )}
          </figure>
        ))}
      </div>

      {sharedNotes.length > 0 && (
        <div className="indicator-row__note indicator-row__note--shared">
          <p className="indicator-row__note-title">Analysis</p>
          {sharedNotes.map((note, i) => (
            <p key={i}>{note}</p>
          ))}
        </div>
      )}
    </article>
  );
}
